// ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import config from '../config';
import passwordService from '../services/passwordService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      notification.error({
        message: 'Email Required',
        description: 'Please enter your email address.',
      });
      return;
    }
    
    setIsLoading(true);

    try {
      // Call the password reset API
      const response = await passwordService.requestPasswordReset(email);
      if (response.success) {
        setIsSubmitted(true);
        notification.success({
          message: 'Reset Link Sent',
          description: 'If your email exists in our system, you will receive password reset instructions.',
        });
      } else {
        throw new Error('Failed to request password reset');
      }
    } catch (err) {
      // Even if there's an error, we show a generic success message for security
      // This prevents user enumeration attacks
      setIsSubmitted(true);
      notification.success({
        message: 'Reset Link Sent',
        description: 'If your email exists in our system, you will receive password reset instructions.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-golden-navy">
        <div className="w-full max-w-md p-8 space-y-8 bg-golden-navy text-white">
          <div className="text-center">
            <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
            <h2 className="mt-6 text-3xl font-bold">Check Your Email</h2>
            <p className="mt-2">
              If your email exists in our system, we've sent you instructions to reset your password.
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-golden-yellow hover:bg-golden-yellow-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-golden-navy">
      <div className="w-full max-w-md p-8 space-y-8 bg-golden-navy text-white">
        <div className="text-center">
          <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
          <h2 className="mt-6 text-3xl font-bold">Reset Your Password</h2>
          <p className="mt-2">Enter your email address and we'll send you instructions to reset your password.</p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="mt-1 block w-full px-3 py-2 bg-white border border-golden-yellow rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-golden-navy bg-white hover:bg-golden-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Send Reset Link'}
            </button>
          </div>

          <div>
            <button
              type="button"
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-transparent hover:bg-golden-yellow/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
              onClick={() => navigate('/login')}
            >
              Back To Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;